import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import './Portfolio.css';

/* -----------------------------------------------------------------
   PORTFOLIO ENTRIES
   ----------------------------------------------------------------- */
const PORTFOLIO_ENTRIES = [
  {
    id: 'harbor-therapy',
    name: 'Harbor Therapy',
    tier: 'Practice',
    category: 'Group therapy practice',
    href: '/portfolio/practice',
    image: '/images/live/harbortherapy.png',
    palette: { bg: '#D6DDD2', accent: '#4A6B5C', shape: '#283A2F' },
    accentToken: 'sage',
    summary:
      'A multi-clinician therapy practice with eight providers, custom multi-step booking, ' +
      "and intake forms that adapt to each clinician's specialty.",
    demonstrates: [
      'Custom multi-step booking flow',
      'Per-staff availability and pricing',
      'Tailored intake forms per provider',
      'Stripe payment integration',
      'Filterable staff directory',
    ],
  },
  {
    id: 'luxe-mane',
    name: 'Luxe Mane Studio',
    tier: 'Starter',
    category: 'Hair extension specialist',
    href: '/portfolio/starter',
    image: '/images/live/luxemane.png',
    palette: { bg: '#E8DFCD', accent: '#A8794D', shape: '#3F3026' },
    accentToken: 'teal',
    summary:
      'A private studio site for an independent hair extension specialist. ' +
      'Inquiry-driven booking, image-led gallery, and a clear single-offer structure.',
    demonstrates: [
      'Single-page architecture done well',
      'Image-forward gallery',
      'Inquiry form with structured intake',
      'Service-card layouts with pricing',
      'Mobile-first responsive design',
    ],
  },
  {
    id: 'brewed-coffee',
    name: 'Brewed',
    tier: 'Commerce',
    category: 'Coffee brand · Product-focused storefront',
    href: '/portfolio/commerce',
    image: '/images/live/brewed.png',
    palette: { bg: '#E7DED4', accent: '#8B5E3C', shape: '#2F221B' },
    accentToken: 'warm',
    summary:
      'A coffee e-commerce demo with product browsing, filtered shop views, product detail pages, cart flow, checkout, quiz, account area, and admin screens.',
    demonstrates: [
      'Multi-page React storefront',
      'Product detail and category routes',
      'Cart and checkout flow',
      'Quiz-based product discovery',
      'Scoped demo styling and standalone routing',
    ],
  },
];

/* -----------------------------------------------------------------
   PORTFOLIO THUMBNAIL — real screenshot, cropped to fill the frame
   ----------------------------------------------------------------- */
function PortfolioThumb({ image, name }) {
  return (
    <img
      src={image}
      alt={`${name} live preview`}
      className="pf-entry-thumb-image"
    />
  );
}

/* -----------------------------------------------------------------
   MARQUEE STRIP
   ----------------------------------------------------------------- */
function MarqueeStrip() {
  const items = [
    'Real pages',
    'Real flows',
    'Real interactions',
    'Click any project',
    'Live & shipping',
    'No mockups, no stock',
  ];

  const row = (key) => (
    <div className="pf-marquee-row" key={key}>
      {items.map((t, i) => (
        <span className="pf-marquee-item" key={i}>
          <span className="pf-marquee-dot" aria-hidden="true" />
          {t}
        </span>
      ))}
    </div>
  );

  return (
    <div className="pf-marquee" aria-hidden="true">
      <div className="pf-marquee-track">
        {row('a')}{row('b')}
      </div>
    </div>
  );
}

/* -----------------------------------------------------------------
   ENTRY VISUAL — tilt + spotlight + parallax
   ----------------------------------------------------------------- */
function EntryVisual({ entry }) {
  const ref = useRef(null);
  const raf = useRef(null);
  const [coords, setCoords] = useState({ x: 50, y: 50, tx: 0, ty: 0, active: false });

  const onMove = useCallback((e) => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    const tx = (x - 50) / 50;
    const ty = (y - 50) / 50;
    cancelAnimationFrame(raf.current);
    raf.current = requestAnimationFrame(() =>
      setCoords({ x, y, tx, ty, active: true })
    );
  }, []);

  const onLeave = useCallback(() => {
    setCoords((c) => ({ ...c, active: false, tx: 0, ty: 0 }));
  }, []);

  useEffect(() => () => cancelAnimationFrame(raf.current), []);

  const tiltStyle = {
    '--pf-mx': `${coords.x}%`,
    '--pf-my': `${coords.y}%`,
    transform: coords.active
      ? `perspective(1100px) rotateX(${(-coords.ty * 3).toFixed(2)}deg) rotateY(${(coords.tx * 3).toFixed(2)}deg) translateY(-6px)`
      : 'perspective(1100px) rotateX(0) rotateY(0) translateY(0)',
  };

  return (
    <Link
      to={entry.href}
      className={`pf-entry-visual ${coords.active ? 'is-hover' : ''}`}
      ref={ref}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      style={tiltStyle}
    >
      <div className="pf-entry-frame-meta">
        <div className="pf-entry-frame-meta-dots"><span /><span /><span /></div>
        <span className="pf-entry-frame-meta-url">{entry.id}.demo / layerandco</span>
        <span className="pf-entry-frame-meta-status">Live</span>
      </div>
      <div className="pf-entry-thumb">
        <div
          className="pf-entry-thumb-inner"
          style={{
            transform: coords.active
              ? `translate3d(${(coords.tx * -10).toFixed(2)}px, ${(coords.ty * -10).toFixed(2)}px, 0)`
              : 'translate3d(0,0,0)',
          }}
        >
          <PortfolioThumb image={entry.image} name={entry.name} />
        </div>
        <div className="pf-entry-spotlight" aria-hidden="true" />
        <div className="pf-entry-overlay">
          <span>Open the live preview →</span>
        </div>
      </div>
    </Link>
  );
}

/* -----------------------------------------------------------------
   ENTRY ROW
   ----------------------------------------------------------------- */
function EntryRow({ entry, idx }) {
  return (
    <article className={`pf-entry pf-entry-${entry.accentToken}`}>
      <EntryVisual entry={entry} />

      <div className="pf-entry-content">
        <div className="pf-entry-meta">
          <span className="pf-entry-tier">Tier · {entry.tier}</span>
        </div>
        <h2>{entry.name}</h2>
        <p className="pf-entry-category">{entry.category}</p>
        <p className="pf-entry-summary">{entry.summary}</p>

        <div className="pf-entry-demonstrates">
          <p className="pf-demonstrates-label">What this demonstrates</p>
          <ul>
            {entry.demonstrates.map((d, i) => (
              <li key={d} style={{ '--i': i }}>{d}</li>
            ))}
          </ul>
        </div>

        <Link to={entry.href} className="pf-entry-link">
          View live preview
        </Link>
      </div>
    </article>
  );
}

/* -----------------------------------------------------------------
   SCROLL PROGRESS BAR
   ----------------------------------------------------------------- */
function ScrollProgress() {
  const [p, setP] = useState(0);

  useEffect(() => {
    const onScroll = () => {
      const h = document.documentElement;
      const max = h.scrollHeight - h.clientHeight;
      setP(max > 0 ? Math.min(1, h.scrollTop / max) : 0);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <div className="pf-progress" aria-hidden="true">
      <div className="pf-progress-bar" style={{ transform: `scaleX(${p})` }} />
    </div>
  );
}

/* -----------------------------------------------------------------
   PORTFOLIO PAGE
   ----------------------------------------------------------------- */
function Portfolio() {
  return (
    <main className="portfolio-page">
      <ScrollProgress />

      {/* -------- HEADER -------- */}
      <section className="pf-header">
        <div className="pf-header-noise" aria-hidden="true" />
        <div className="pf-container">
          <div className="pf-header-row">
            <div>
              <p className="pf-eyebrow">Selected work</p>
              <h1>
                Built to <em>increase revenue,</em><br />
                not just look <span className="pf-h1-strike">good</span><span className="pf-h1-cursor" aria-hidden="true" />.
              </h1>
              <p className="pf-lede">
                Three example builds, one per tier, showing how the work actually functions.
                Real pages, real flows, real interactions. Click through any project to see it in action.
              </p>
            </div>
            <div className="pf-header-stack">
              <div className="pf-header-stack-corner" aria-hidden="true">
                <span /><span /><span /><span />
              </div>
              <div className="pf-header-stack-row">
                <span>Tiers shown</span>
                <span>Starter · Practice · Commerce</span>
              </div>
              <div className="pf-header-stack-row">
                <span>Live demos</span>
                <span>Fully clickable</span>
              </div>
              <div className="pf-header-stack-row">
                <span>Want a closer look?</span>
                <span>
                  <Link to="/contact" style={{ borderBottom: '1px solid currentColor', paddingBottom: '2px' }}>
                    Get in touch ↗
                  </Link>
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <MarqueeStrip />

      {/* -------- ENTRIES -------- */}
      <section className="pf-entries">
        <div className="pf-entries-rail" aria-hidden="true">
          <span className="pf-entries-rail-tick" />
          <span className="pf-entries-rail-tick" />
          <span className="pf-entries-rail-tick" />
        </div>
        <div className="pf-container">
          {PORTFOLIO_ENTRIES.map((entry, idx) => (
            <EntryRow key={entry.id} entry={entry} idx={idx} />
          ))}
        </div>
      </section>

      {/* -------- CTA -------- */}
      <section className="pf-cta">
        <div className="pf-cta-grid" aria-hidden="true" />
        <div className="pf-cta-glow" aria-hidden="true" />
        <div className="pf-cta-orbit" aria-hidden="true">
          <span /><span /><span />
        </div>
        <div className="pf-container-narrow pf-cta-inner">
          <p className="pf-cta-eyebrow">Next step</p>
          <h2>Ready to build something <em>real?</em></h2>
          <p>
            These are live previews. Your project gets the same care, built around your business.
            Get in touch and we&apos;ll scope it out.
          </p>
          <div className="pf-cta-actions">
            <Link to="/contact" className="pf-btn pf-btn-primary">
              Get in touch
            </Link>
            <Link to="/services" className="pf-btn pf-btn-secondary">
              View services
            </Link>
          </div>
        </div>
      </section>

    </main>
  );
}

export default Portfolio;